import { useAuth0 } from '@auth0/auth0-react';
import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useDebounceValue } from '@/hooks';

// ES 2015
import { EnergyUsage } from './EnergyUsage';
import { EnergyUsageInput } from './EnergyUsageInput';
import { EnergyUsageResponse } from './EnergyUsageResponse';

const {
  VITE_GREENSEE_API_URL,
  VITE_AUTH0_AUDIENCE,
  VITE_APP_ENERGY_USAGE_QUERIES_DEBOUNCE_MS,
} = import.meta.env;

const EnergyUsageQueriesDebounceMs = parseInt(
  VITE_APP_ENERGY_USAGE_QUERIES_DEBOUNCE_MS,
);

export function usePostEnergyUsage(
  params: EnergyUsageInput,
): [UseQueryResult<EnergyUsage[], Error>, () => void] {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const body = JSON.stringify(params);

  const debouncedKey = useDebounceValue(body);
  const queryKey = ['greensee-api-post-energy-usage', debouncedKey];

  const result = useQuery({
    queryKey,
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: VITE_AUTH0_AUDIENCE,
          redirect_uri: window.location.origin,
        },
        detailedResponse: true,
      });

      const response = await fetch(`${VITE_GREENSEE_API_URL}/energy-usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.access_token}`,
        },
        body,
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(
          `Error while fetching energy usage: Invalid Content-Type response ${response.status}`,
        );
      }

      const result: EnergyUsageResponse = await response.json();

      if (!response.ok) {
        throw new Error(
          `Error while fetching energy usage: ${response.status} ${result.message || ''}`,
        );
      }

      const dataset = result.data || [];

      // const dataset: EnergyUsage[] = [];

      // const from = dayjs(params.start);
      // const to = dayjs(params.end);

      // for (
      //   let curr = from.clone();
      //   curr.isBefore(to);
      //   curr = curr.add(1, 'month')
      // ) {
      //   const currentMonth = curr
      //
      //     .startOf('month')
      //     .format('YYYY-MM-DDTHH:mm:ssZZ');

      //   const rsData = result.data.find(({ month }) =>
      //     month.localeCompare(currentMonth),
      //   );

      //   const data: EnergyUsage = rsData
      //     ? rsData
      //     : {
      //         month: currentMonth,
      //         active_power_kW: 0,
      //         active_power_kW_transition: 0,
      //         active_power_kW_stable: 0,
      //         co2eq_kg_per_hour: 0,
      //         co2eq_kg_per_hour_stable: 0,
      //         co2eq_kg_per_hour_transition: 0,
      //       };

      //   dataset.push(data);
      // }

      // console.log('dataset:', dataset.length);

      return dataset;
    },
  });

  return [result, () => queryClient.invalidateQueries({ queryKey })];
}
